import React, { useEffect, useState } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Button, Chip, ToggleButtonGroup, ToggleButton, Box, Typography } from '@mui/material';
import { PopupStoresCrawlService, PopupStoreCrawl } from '../../services/popupStoresCrawl.service';
import CrawlDetailModal from './CrawlDetailModal';
import Swal from 'sweetalert2';

export default function PopupStoresCrawlPage() {
  const [rows, setRows] = useState<PopupStoreCrawl[]>([]);
  const [filter, setFilter] = useState<string>('pending');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await PopupStoresCrawlService.getAll(filter || undefined);
      setRows(response.data);
    } catch (error) {
      console.error('Failed to load crawl data:', error);
      Swal.fire('오류', '데이터를 불러오는데 실패했습니다', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: number) => {
    const result = await Swal.fire({
      title: '승인하시겠습니까?',
      text: '승인하면 팝업스토어 목록에 추가됩니다.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: '승인',
      cancelButtonText: '취소',
      customClass: {
        container: 'swal-zindex-high'
      },
      didOpen: () => {
        const container = document.querySelector('.swal-zindex-high') as HTMLElement;
        if (container) container.style.zIndex = '20000';
      }
    });

    if (result.isConfirmed) {
      try {
        // JWT 토큰에서 자동으로 adminId를 가져옴
        await PopupStoresCrawlService.approve(id);
        Swal.fire({
          title: '승인 완료',
          icon: 'success',
          customClass: { container: 'swal-zindex-high' },
          didOpen: () => {
            const container = document.querySelector('.swal-zindex-high') as HTMLElement;
            if (container) container.style.zIndex = '20000';
          }
        });
        loadData();
      } catch (error) {
        console.error('Failed to approve:', error);
        Swal.fire({
          title: '오류',
          text: '승인에 실패했습니다',
          icon: 'error',
          customClass: { container: 'swal-zindex-high' },
          didOpen: () => {
            const container = document.querySelector('.swal-zindex-high') as HTMLElement;
            if (container) container.style.zIndex = '20000';
          }
        });
      }
    }
  };

  const handleReject = async (id: number) => {
    const result = await Swal.fire({
      title: '거부하시겠습니까?',
      input: 'textarea',
      inputLabel: '거부 사유 (선택)',
      showCancelButton: true,
      confirmButtonText: '거부',
      cancelButtonText: '취소',
      customClass: {
        container: 'swal-zindex-high'
      },
      didOpen: () => {
        const container = document.querySelector('.swal-zindex-high') as HTMLElement;
        if (container) container.style.zIndex = '20000';
      }
    });

    if (result.isConfirmed) {
      try {
        await PopupStoresCrawlService.reject(id, result.value);
        Swal.fire({
          title: '거부 완료',
          icon: 'success',
          customClass: { container: 'swal-zindex-high' },
          didOpen: () => {
            const container = document.querySelector('.swal-zindex-high') as HTMLElement;
            if (container) container.style.zIndex = '20000';
          }
        });
        loadData();
      } catch (error) {
        console.error('Failed to reject:', error);
        Swal.fire({
          title: '오류',
          text: '거부에 실패했습니다',
          icon: 'error',
          customClass: { container: 'swal-zindex-high' },
          didOpen: () => {
            const container = document.querySelector('.swal-zindex-high') as HTMLElement;
            if (container) container.style.zIndex = '20000';
          }
        });
      }
    }
  };

  const handleRunCrawl = async () => {
    const result = await Swal.fire({
      title: '크롤링을 실행하시겠습니까?',
      text: '팝플리/팝가에서 신규 팝업을 수집합니다 (시간이 소요될 수 있습니다)',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: '실행',
      cancelButtonText: '취소',
      customClass: {
        container: 'swal-zindex-high'
      },
      didOpen: () => {
        const container = document.querySelector('.swal-zindex-high') as HTMLElement;
        if (container) container.style.zIndex = '20000';
      }
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        const response = await PopupStoresCrawlService.runCrawlNow();
        const data = response.data;
        Swal.fire({
          title: '크롤링 완료',
          text: `성공: ${data.success}건, 실패: ${data.failed}건, 중복: ${data.skipped}건`,
          icon: 'success',
          customClass: { container: 'swal-zindex-high' },
          didOpen: () => {
            const container = document.querySelector('.swal-zindex-high') as HTMLElement;
            if (container) container.style.zIndex = '20000';
          }
        });
        loadData();
      } catch (error) {
        console.error('Failed to run crawl:', error);
        Swal.fire({
          title: '오류',
          text: '크롤링 실행에 실패했습니다',
          icon: 'error',
          customClass: { container: 'swal-zindex-high' },
          didOpen: () => {
            const container = document.querySelector('.swal-zindex-high') as HTMLElement;
            if (container) container.style.zIndex = '20000';
          }
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'actions',
      headerName: '',
      width: 280,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSelectedId(params.row.id)}
          >
            상세보기
          </Button>
          {params.row.status === 'pending' && (
            <>
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={() => setSelectedId(params.row.id)}
              >
                승인
              </Button>
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={() => setSelectedId(params.row.id)}
              >
                거부
              </Button>
            </>
          )}
        </Box>
      ),
    },
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'status',
      headerName: '상태',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={
            params.value === 'approved'
              ? '승인됨'
              : params.value === 'rejected'
              ? '거부됨'
              : '대기중'
          }
          color={
            params.value === 'approved'
              ? 'success'
              : params.value === 'rejected'
              ? 'error'
              : 'default'
          }
          size="small"
        />
      ),
    },
    {
      field: 'crawlSource',
      headerName: '출처',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value === 'popply' ? '팝플리' : '팝가'}
          color={params.value === 'popply' ? 'primary' : 'secondary'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'title',
      headerName: '제목',
      width: 300,
      valueGetter: (params) => params.row.processedData?.title || params.row.rawData?.title,
    },
    {
      field: 'start_date',
      headerName: '시작일',
      width: 120,
      valueGetter: (params) => params.row.processedData?.start_date || params.row.rawData?.start_date,
    },
    {
      field: 'end_date',
      headerName: '종료일',
      width: 120,
      valueGetter: (params) => params.row.processedData?.end_date || params.row.rawData?.end_date,
    },
    {
      field: 'created_at',
      headerName: '수집일',
      width: 180,
      valueFormatter: (params) => new Date(params.value).toLocaleString('ko-KR'),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">크롤링 데이터 관리</Typography>
        <Button variant="contained" color="primary" onClick={handleRunCrawl} disabled={loading}>
          🔄 크롤링 실행
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(e, val) => val !== null && setFilter(val)}
        >
          <ToggleButton value="pending">대기중</ToggleButton>
          <ToggleButton value="approved">승인됨</ToggleButton>
          <ToggleButton value="rejected">거부됨</ToggleButton>
          <ToggleButton value="">전체</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: 20 } },
        }}
        pageSizeOptions={[10, 20, 50]}
        autoHeight
        disableRowSelectionOnClick
      />

      {selectedId && (
        <CrawlDetailModal
          id={selectedId}
          onClose={() => setSelectedId(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onRefresh={loadData}
        />
      )}
    </Box>
  );
}
